/**
 * Image data store
 * Manages uploaded images and their metadata
 */

import { create } from 'zustand';
import { ImageFile, ProcessedImage, AnyOperation, ImageHistoryState, OperationType, ImportOperation } from '../types';
import { generateMessageId } from '../types/worker';

interface ImageState {
  // State
  images: ImageFile[];
  selectedImageId: string | null;
  processedImages: Map<string, ProcessedImage>;

  // History management
  history: Map<string, ImageHistoryState[]>;
  historyIndex: Map<string, number>;

  // Memory management
  maxImages: number;
  cleanupThreshold: number;
  cleanupScheduled: boolean;

  // Actions
  addImages: (files: File[]) => Promise<void>;
  removeImage: (id: string) => void;
  selectImage: (id: string) => void;
  clearAll: () => void;

  // Memory management actions
  scheduleCleanup: () => void;
  performCleanup: (keepCount: number) => void;

  // Operation management
  addOperation: (imageId: string, operation: AnyOperation) => void;
  getOperations: (imageId: string) => AnyOperation[];
  clearOperations: (imageId: string) => void;

  // Processed images
  setProcessedImage: (imageId: string, processed: ProcessedImage) => void;
  getProcessedImage: (imageId: string) => ProcessedImage | undefined;

  // History actions
  undo: (imageId: string) => void;
  redo: (imageId: string) => void;
  canUndo: (imageId: string) => boolean;
  canRedo: (imageId: string) => boolean;
  applyOperation: (imageId: string, newFile: File, operation: AnyOperation, width?: number, height?: number) => void;

  // Helpers
  getImageById: (id: string) => ImageFile | undefined;
  getSelectedImage: () => ImageFile | undefined;
}

export const useImageStore = create<ImageState>((set, get) => ({
  // Initial state
  images: [],
  selectedImageId: null,
  processedImages: new Map(),
  history: new Map(),
  historyIndex: new Map(),

  // Memory management settings
  maxImages: 50, // Maximum number of images to keep in memory
  cleanupThreshold: 20, // Trigger cleanup when exceeding this count
  cleanupScheduled: false,

  /**
   * Add images to the store
   * Validates files and extracts metadata
   */
  addImages: async (files: File[]) => {
    const { images, cleanupThreshold, cleanupScheduled } = get();

    // Process each file
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        const id = generateMessageId();

        // Create blob URL
        const url = URL.createObjectURL(file);

        // Extract image dimensions
        const dimensions = await getImageDimensions(file);

        return {
          id,
          file,
          url,
          width: dimensions.width,
          height: dimensions.height,
          size: file.size,
          format: getFormatFromFile(file),
          fileName: file.name,
        } as ImageFile;
      })
    );

    // Initialize history for new images
    const newHistory = new Map(get().history);
    const newHistoryIndex = new Map(get().historyIndex);

    processedFiles.forEach(img => {
      newHistory.set(img.id, [{
        file: img.file,
        url: img.url,
        width: img.width,
        height: img.height,
        operation: { type: OperationType.IMPORT, timestamp: Date.now() } as ImportOperation,
        timestamp: Date.now()
      }]);
      newHistoryIndex.set(img.id, 0);
    });

    // Add to store
    set((state) => ({
      images: [...state.images, ...processedFiles],
      selectedImageId: processedFiles[0]?.id ?? state.selectedImageId,
      history: newHistory,
      historyIndex: newHistoryIndex,
    }));

    // Check if cleanup is needed
    const newImageCount = images.length + processedFiles.length;
    if (newImageCount > cleanupThreshold && !cleanupScheduled) {
      get().scheduleCleanup();
    }
  },

  /**
   * Schedule memory cleanup
   */
  scheduleCleanup: () => {
    const { cleanupScheduled } = get();
    if (cleanupScheduled) return;

    set({ cleanupScheduled: true });

    // Delay cleanup to avoid interrupting user workflow
    setTimeout(() => {
      const { maxImages } = get();
      get().performCleanup(maxImages);
    }, 1000);
  },

  /**
   * Perform cleanup of old images
   */
  performCleanup: (keepCount: number) => {
    const { images } = get();

    if (images.length <= keepCount) {
      set({ cleanupScheduled: false });
      return;
    }

    // Remove oldest images beyond the keep count
    const toRemove = images.slice(0, images.length - keepCount);

    // Revoke blob URLs
    toRemove.forEach((img) => {
      try {
        URL.revokeObjectURL(img.url);
      } catch (e) {
        console.warn('Failed to revoke URL during cleanup:', img.id, e);
      }
    });

    set((state) => ({
      images: state.images.slice(-keepCount),
      selectedImageId: state.selectedImageId,
      cleanupScheduled: false,
    }));

    console.log(`Cleaned up ${toRemove.length} old images to free memory`);
  },

  /**
   * Remove an image from the store
   */
  removeImage: (id: string) => {
    set((state) => {
      const imageToRemove = state.images.find((img) => img.id === id);

      // Revoke object URL to free memory
      if (imageToRemove) {
        try {
          URL.revokeObjectURL(imageToRemove.url);
        } catch (e) {
          console.warn('Failed to revoke URL for image:', id, e);
        }

        // Cleanup history URLs
        const history = state.history.get(id);
        if (history) {
          history.forEach(h => {
            if (h.url !== imageToRemove.url) {
              try {
                URL.revokeObjectURL(h.url);
              } catch (e) {
                console.warn('Failed to revoke history URL:', e);
              }
            }
          });
        }
      }

      // Remove from images array
      const images = state.images.filter((img) => img.id !== id);

      // Update selected image if needed
      let selectedImageId = state.selectedImageId;
      if (state.selectedImageId === id) {
        selectedImageId = images.length > 0 ? images[0].id : null;
      }

      // Remove from processed images
      const processedImages = new Map(state.processedImages);
      processedImages.delete(id);

      // Remove from history
      const history = new Map(state.history);
      const historyIndex = new Map(state.historyIndex);
      history.delete(id);
      historyIndex.delete(id);

      return { images, selectedImageId, processedImages, history, historyIndex };
    });
  },

  /**
   * Select an image
   */
  selectImage: (id: string) => {
    set({ selectedImageId: id });
  },

  /**
   * Clear all images
   */
  clearAll: () => {
    const { images } = get();

    // Revoke all object URLs
    images.forEach((img) => {
      try {
        URL.revokeObjectURL(img.url);
        // Cleanup history as well
        const history = get().history.get(img.id);
        history?.forEach(h => {
          try {
            URL.revokeObjectURL(h.url);
          } catch (e) {
            console.warn('Failed to revoke history URL during clearAll:', e);
          }
        });
      } catch (e) {
        console.warn('Failed to revoke URL during clearAll:', img.id, e);
      }
    });

    set({
      images: [],
      selectedImageId: null,
      processedImages: new Map(),
      history: new Map(),
      historyIndex: new Map(),
    });
  },

  /**
   * Add an operation to an image
   */
  addOperation: (imageId: string, operation: AnyOperation) => {
    // Operations are stored in ProcessedImage, not in ImageFile
    // This is a placeholder for future use
    console.log('Operation added:', imageId, operation);
  },

  /**
   * Get all operations for an image
   */
  getOperations: (imageId: string) => {
    const { processedImages } = get();
    const processed = processedImages.get(imageId);
    return (processed?.operations ?? []) as AnyOperation[];
  },

  /**
   * Clear operations for an image
   */
  clearOperations: (imageId: string) => {
    set((state) => {
      const existing = state.processedImages.get(imageId);

      // Early return if item doesn't exist - avoid unnecessary Map copy
      if (!existing) {
        return state;
      }

      const processedImages = new Map(state.processedImages);
      processedImages.set(imageId, { ...existing, operations: [] });

      return { processedImages };
    });
  },

  /**
   * Set a processed image
   */
  setProcessedImage: (imageId: string, processed: ProcessedImage) => {
    set((state) => {
      const processedImages = new Map(state.processedImages);
      processedImages.set(imageId, processed);
      return { processedImages };
    });
  },

  /**
   * Get a processed image
   */
  getProcessedImage: (imageId: string) => {
    return get().processedImages.get(imageId);
  },

  /**
   * Get an image by ID
   */
  getImageById: (id: string) => {
    return get().images.find((img) => img.id === id);
  },

  /**
   * Get the currently selected image
   */
  getSelectedImage: () => {
    const { images, selectedImageId } = get();
    return images.find((img) => img.id === selectedImageId);
  },

  // ============================================================================
  // History Actions
  // ============================================================================

  /**
   * Apply an operation and save to history
   */
  applyOperation: (imageId: string, newFile: File, operation: AnyOperation, newWidth?: number, newHeight?: number) => {
    const { images, history, historyIndex } = get();
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;

    const updateHistory = (w: number, h: number) => {
      const imageHistory = history.get(imageId) || [];
      const currentIndex = historyIndex.get(imageId) ?? -1;

      // Truncate future history
      const newHistory = imageHistory.slice(0, currentIndex + 1);

      const newState: ImageHistoryState = {
        file: newFile,
        url: URL.createObjectURL(newFile),
        width: w,
        height: h,
        operation,
        timestamp: Date.now()
      };

      newHistory.push(newState);

      // No history limit - allow unlimited undo/redo
      // Memory management: Only revoke URLs for states that are no longer accessible
      // (This happens automatically during cleanup when images are removed)

      console.log('📝 Operation applied:', operation.type, `(${imageId.slice(0, 8)})`);

      set(state => ({
        images: state.images.map(img =>
          img.id === imageId
            ? { ...img, file: newFile, url: newState.url, width: w, height: h, size: newFile.size }
            : img
        ),
        history: new Map(state.history).set(imageId, newHistory),
        historyIndex: new Map(state.historyIndex).set(imageId, newHistory.length - 1),
      }));
    };

    if (newWidth !== undefined && newHeight !== undefined) {
      updateHistory(newWidth, newHeight);
    } else {
      getImageDimensions(newFile).then(({ width, height }) => updateHistory(width, height));
    }
  },

  /**
   * Undo last operation
   */
  undo: (imageId: string) => {
    const { history, historyIndex } = get();
    const imageHistory = history.get(imageId);
    const currentIndex = historyIndex.get(imageId);

    if (!imageHistory || currentIndex === undefined || currentIndex <= 0) return;

    const newIndex = currentIndex - 1;
    const previousState = imageHistory[newIndex];

    console.log('⏪ Undo:', imageHistory[currentIndex].operation.type, '→', previousState.operation.type);

    set(state => ({
      images: state.images.map(img =>
        img.id === imageId
          ? { ...img, file: previousState.file, url: previousState.url, width: previousState.width, height: previousState.height, size: previousState.file.size }
          : img
      ),
      historyIndex: new Map(state.historyIndex).set(imageId, newIndex)
    }));
  },

  /**
   * Redo last undone operation
   */
  redo: (imageId: string) => {
    const { history, historyIndex } = get();
    const imageHistory = history.get(imageId);
    const currentIndex = historyIndex.get(imageId);

    if (!imageHistory || currentIndex === undefined || currentIndex >= imageHistory.length - 1) return;

    const newIndex = currentIndex + 1;
    const nextState = imageHistory[newIndex];

    console.log('⏩ Redo:', imageHistory[currentIndex].operation.type, '→', nextState.operation.type);

    set(state => ({
      images: state.images.map(img =>
        img.id === imageId
          ? { ...img, file: nextState.file, url: nextState.url, width: nextState.width, height: nextState.height, size: nextState.file.size }
          : img
      ),
      historyIndex: new Map(state.historyIndex).set(imageId, newIndex)
    }));
  },

  /**
   * Check if undo is available
   */
  canUndo: (imageId: string) => {
    const currentIndex = get().historyIndex.get(imageId);
    return currentIndex !== undefined && currentIndex > 0;
  },

  /**
   * Check if redo is available
   */
  canRedo: (imageId: string) => {
    const { history, historyIndex } = get();
    const list = history.get(imageId);
    const index = historyIndex.get(imageId);
    return !!list && index !== undefined && index < list.length - 1;
  },
}));

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get image dimensions from a file
 */
async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Get image format from file
 */
function getFormatFromFile(file: File): string {
  const type = file.type;
  if (type.startsWith('image/')) {
    return type as string;
  }

  // Fallback: infer from extension
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension) {
    const formatMap: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      webp: 'image/webp',
      gif: 'image/gif',
      bmp: 'image/bmp',
      tiff: 'image/tiff',
      heic: 'image/heic',
    };
    return formatMap[extension] || 'image/jpeg';
  }

  return 'image/jpeg';
}
